import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { createPortal } from "react-dom";
import { ChevronDownSvg } from "../../../svgFiles/ChevronDownSvg";
import { AddSvg } from "../../../svgFiles/AddSvg";

// Keep Bootstrap/Popper positioning and keyboard handling while escaping scroll clipping.
const PortalMenu = React.forwardRef(function PortalMenu({ children, ...props }, ref) {
  return createPortal(
    <div className="custoom-dropdown prescription-dropdown-portal">
      <div {...props} ref={ref}>{children}</div>
    </div>,
    document.body
  );
});

const CustomDropdown = ({
  label = "Select Option",
  options = [],
  onSelect,
  lockLabel = false,
  className,
  barIcon,
  value,
  error = false,
  disabled = false,
  showValue = false,
  searchable = false,
  portalMenu = false,
  searchPlaceholder = "Search…",
}) => {
  const reactId = useId();
  const dropdownId = `custom-dropdown-${reactId}`;
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((opt) => String(opt.value) === String(value));

  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((opt) => {
      const labelText = String(opt.label || "").toLowerCase();
      const valueText = String(opt.value ?? "").toLowerCase();
      return labelText.includes(term) || valueText.includes(term);
    });
  }, [options, query, searchable]);

  const closeMenu = () => {
    setOpen(false);
    setQuery("");
  };

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
        closeMenu();
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeMenu();
    };

    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("touchstart", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("touchstart", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !searchable) return undefined;
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open, searchable]);

  const handleSelect = (opt) => {
    onSelect?.(opt);
    closeMenu();
  };

  const toggleLabel = showValue
    ? `${label}${selected ? ` : ${selected.value}` : ""}`
    : lockLabel
      ? label
      : selected?.label || label;

  // Searchable: fully controlled custom menu (Bootstrap dropdown fights close/focus).
  if (searchable) {
    return (
      <div
        ref={rootRef}
        className={`${className || ""} custoom-dropdown dropdown${
          disabled ? " is-disabled" : ""
        }${open ? " show" : ""}`.trim()}
      >
        <button
          type="button"
          id={dropdownId}
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          className={`dropdown-toggle d-flex justify-content-between align-items-center w-100 ${
            error ? "border border-danger" : ""
          }`}
          style={disabled ? { cursor: "not-allowed" } : undefined}
          onClick={() => {
            if (disabled) return;
            setOpen((prev) => {
              if (prev) {
                setQuery("");
                return false;
              }
              return true;
            });
          }}
        >
          {barIcon ? (
            <span>{barIcon}</span>
          ) : (
            <>
              <span className="flex-grow-1 text-start d-flex align-items-center gap-2">
                {!lockLabel && selected?.icon ? selected.icon : null}
                <span className="text-truncate">{toggleLabel}</span>
              </span>
              {lockLabel ? <AddSvg /> : <ChevronDownSvg />}
            </>
          )}
        </button>

        {open ? (
          <div
            className="dropdown-menu show custoom-dropdown__menu--searchable"
            role="listbox"
            aria-labelledby={dropdownId}
          >
            <div className="custoom-dropdown__search">
              <input
                ref={searchRef}
                type="search"
                className="form-control form-control-sm"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="custoom-dropdown__list">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-muted small">No matches</div>
              ) : (
                filteredOptions.map((opt) => {
                  const isActive =
                    String(selected?.value) === String(opt.value);
                  return (
                    <button
                      key={String(opt.value)}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      className={`dropdown-item d-flex align-items-center gap-2${
                        isActive ? " active" : ""
                      }`}
                      onClick={() => handleSelect(opt)}
                    >
                      {opt?.icon ? opt.icon : null}
                      <span className="text-truncate">{opt.label}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div ref={rootRef}>
      <Dropdown
        show={portalMenu ? open : undefined}
        onToggle={portalMenu ? (nextOpen) => setOpen(nextOpen) : undefined}
        className={`${className || ""} custoom-dropdown${
          disabled ? " is-disabled" : ""
        }`.trim()}
      >
        <Dropdown.Toggle
          disabled={disabled}
          id={dropdownId}
          variant="unset"
          className={`d-flex justify-content-between align-items-center w-100 ${
            error ? "border border-danger" : ""
          }`}
          style={disabled ? { cursor: "not-allowed" } : undefined}
        >
          {barIcon ? (
            <span>{barIcon}</span>
          ) : (
            <>
              {showValue ? (
                <span className="flex-grow-1 text-start">{toggleLabel}</span>
              ) : (
                <span className="flex-grow-1 text-start d-flex align-items-center gap-2">
                  {!lockLabel && selected?.icon ? selected.icon : null}
                  <span className="text-truncate">
                    {lockLabel ? label : selected?.label || label}
                  </span>
                </span>
              )}
              {lockLabel ? <AddSvg /> : <ChevronDownSvg />}
            </>
          )}
        </Dropdown.Toggle>

        <Dropdown.Menu
          ref={menuRef}
          as={portalMenu ? PortalMenu : undefined}
          popperConfig={portalMenu ? { strategy: "fixed" } : undefined}
          style={portalMenu ? {
            width: rootRef.current?.getBoundingClientRect().width,
            minWidth: rootRef.current?.getBoundingClientRect().width,
            maxHeight: "min(280px, calc(100dvh - 32px))",
            overflowY: "auto",
            zIndex: 1080,
          } : undefined}
        >
          {options.map((opt) => (
            <Dropdown.Item
              key={String(opt.value)}
              onClick={() => onSelect?.(opt)}
              active={String(selected?.value) === String(opt.value)}
              className="d-flex align-items-center gap-2"
            >
              {opt?.icon && opt?.icon} {opt.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default CustomDropdown;
